import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL

const SchoolPurchase = () => {
  const navigate = useNavigate()
  const { user, session } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Form fields - Invoice/Billing data
  const [schoolName, setSchoolName] = useState('')
  const [streetAddress, setStreetAddress] = useState('')
  const [city, setCity] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [ico, setIco] = useState('')
  const [dic, setDic] = useState('')
  const [icDph, setIcDph] = useState('')
  const [officialEmail, setOfficialEmail] = useState('')
  const [billingEmail, setBillingEmail] = useState('')
  const [contactName, setContactName] = useState('')
  const [phone, setPhone] = useState('')
  const [note, setNote] = useState('')

  // Confirmations
  const [ageConfirmed, setAgeConfirmed] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)

  // Phase 2: Electronic invoice consent + postal fallback
  const [electronicInvoiceConsent, setElectronicInvoiceConsent] = useState(true)
  const [postalInvoiceName, setPostalInvoiceName] = useState('')
  const [postalInvoiceStreet, setPostalInvoiceStreet] = useState('')
  const [postalInvoicePostalCode, setPostalInvoicePostalCode] = useState('')
  const [postalInvoiceCity, setPostalInvoiceCity] = useState('')
  const [postalInvoiceCountry, setPostalInvoiceCountry] = useState('Slovensko')

  // Validation
  const [validationErrors, setValidationErrors] = useState({})

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return re.test(email)
  }

  const validateForm = () => {
    const errors = {}

    if (!schoolName.trim()) {
      errors.schoolName = 'Zadajte názov školy.'
    }

    if (!streetAddress.trim()) {
      errors.streetAddress = 'Zadajte ulicu a číslo.'
    }

    if (!city.trim()) {
      errors.city = 'Zadajte mesto.'
    }

    if (!postalCode.trim()) {
      errors.postalCode = 'Zadajte PSČ.'
    }

    if (!ico.trim()) {
      errors.ico = 'Zadajte IČO.'
    }

    if (!officialEmail.trim()) {
      errors.officialEmail = 'Zadajte platný oficiálny školský e-mail.'
    } else if (!validateEmail(officialEmail)) {
      errors.officialEmail = 'Zadajte platný oficiálny školský e-mail.'
    }

    if (!billingEmail.trim()) {
      errors.billingEmail = 'Zadajte platný fakturačný e-mail.'
    } else if (!validateEmail(billingEmail)) {
      errors.billingEmail = 'Zadajte platný fakturačný e-mail.'
    }

    if (!contactName.trim()) {
      errors.contactName = 'Zadajte meno kontaktnej osoby.'
    }

    if (!ageConfirmed) {
      errors.ageConfirmed = 'Pred pokračovaním je potrebné potvrdiť vekové vyhlásenie.'
    }

    if (!termsAccepted) {
      errors.termsAccepted = 'Pred pokračovaním je potrebné potvrdiť podmienky používania.'
    }

    // Phase 2: Postal fallback validation when electronic consent is OFF
    if (!electronicInvoiceConsent) {
      if (!postalInvoiceName.trim()) {
        errors.postalInvoiceName = 'Zadajte meno príjemcu pre poštovú faktúru.'
      }
      if (!postalInvoiceStreet.trim()) {
        errors.postalInvoiceStreet = 'Zadajte ulicu pre poštové doručenie.'
      }
      if (!postalInvoicePostalCode.trim()) {
        errors.postalInvoicePostalCode = 'Zadajte PSČ pre poštové doručenie.'
      }
      if (!postalInvoiceCity.trim()) {
        errors.postalInvoiceCity = 'Zadajte mesto pre poštové doručenie.'
      }
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handlePurchase = async () => {
    // Validate form
    if (!validateForm()) {
      return
    }

    if (!user) {
      navigate('/login', { state: { from: '/school-purchase' } })
      return
    }

    try {
      setLoading(true)
      setError('')

      const originUrl = window.location.origin
      const payload = {
        origin_url: originUrl,
        product_type: 'financial_literacy_school_package',
        school_data: {
          school_name: schoolName.trim(),
          street_address: streetAddress.trim(),
          city: city.trim(),
          postal_code: postalCode.trim(),
          ico: ico.trim(),
          dic: dic.trim() || null,
          ic_dph: icDph.trim() || null,
          official_school_email: officialEmail.trim(),
          billing_email: billingEmail.trim(),
          contact_name: contactName.trim(),
          phone: phone.trim() || null,
          note: note.trim() || null,
        },
        electronic_invoice_consent: electronicInvoiceConsent,
        postal_invoice_address: !electronicInvoiceConsent ? {
          name: postalInvoiceName.trim(),
          street: postalInvoiceStreet.trim(),
          postal_code: postalInvoicePostalCode.trim(),
          city: postalInvoiceCity.trim(),
          country: postalInvoiceCountry.trim() || 'Slovensko'
        } : null,
        metadata: {
          source: 'school_purchase_page',
          product_type: 'financial_literacy_school_package'
        }
      }

      const response = await fetch(`${BACKEND_URL}/api/school-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify(payload)
      })

      let data
      try {
        data = await response.json()
      } catch (parseError) {
        console.error('Failed to parse response:', parseError)
        throw new Error('Neplatná odpoveď zo servera')
      }

      if (!response.ok) {
        throw new Error(data?.detail || data?.errors?.[0] || 'Chyba pri vytváraní platby')
      }

      if (data.url) {
        window.location.href = data.url
      } else {
        throw new Error('Chýba URL pre platbu')
      }

    } catch (err) {
      console.error('Purchase error:', err)
      setError(err?.message || 'Nastala chyba pri spracovaní platby')
    } finally {
      setLoading(false)
    }
  }

  const isPurchaseDisabled = loading || !ageConfirmed || !termsAccepted

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4">
              🏫 Pre školy
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Objednávka programu finančnej gramotnosti pre školu
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Vyplňte základné údaje o škole a kontaktnej osobe. Po potvrdení podmienok budete presmerovaní na platbu.
            </p>
          </div>

          {/* Pricing Card */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <div className="text-center mb-6">
              <div className="text-5xl font-bold text-green-600 mb-2">250 €</div>
              <div className="text-gray-600">/ ročný prístup</div>
            </div>
            <div className="bg-green-50 rounded-xl p-4">
              <div className="text-sm text-gray-700 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>Neomedzený počet žiakov a učiteľov</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>Prístup ku všetkým scenárom (4.–9. ročník)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>Nové scenáre pridávané priebežne</span>
                </div>
              </div>
            </div>
          </div>

          {/* School Details Form */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Údaje školy</h2>
            
            <div className="space-y-6">
              {/* School Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Názov školy <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={schoolName}
                  onChange={(e) => setSchoolName(e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    validationErrors.schoolName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="napr. ZŠ Ľubeník"
                />
                {validationErrors.schoolName && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.schoolName}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">Uveďte oficiálny názov školy.</p>
              </div>

              {/* Street Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ulica a číslo <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={streetAddress}
                  onChange={(e) => setStreetAddress(e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    validationErrors.streetAddress ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="napr. Hlavná 123"
                />
                {validationErrors.streetAddress && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.streetAddress}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">Uveďte fakturačnú adresu školy.</p>
              </div>

              {/* Postal Code and City - 2 columns */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PSČ <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      validationErrors.postalCode ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="napr. 976 55"
                  />
                  {validationErrors.postalCode && (
                    <p className="text-red-500 text-sm mt-1">{validationErrors.postalCode}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Uveďte PSČ fakturačnej adresy.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mesto <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      validationErrors.city ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="napr. Ľubeník"
                  />
                  {validationErrors.city && (
                    <p className="text-red-500 text-sm mt-1">{validationErrors.city}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Uveďte mesto fakturačnej adresy.</p>
                </div>
              </div>

              {/* IČO, DIČ, IČ DPH - 3 columns */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    IČO <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={ico}
                    onChange={(e) => setIco(e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      validationErrors.ico ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="napr. 12345678"
                  />
                  {validationErrors.ico && (
                    <p className="text-red-500 text-sm mt-1">{validationErrors.ico}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Uveďte identifikačné číslo organizácie.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    DIČ
                  </label>
                  <input
                    type="text"
                    value={dic}
                    onChange={(e) => setDic(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="napr. 2021234567"
                  />
                  <p className="text-xs text-gray-500 mt-1">Voliteľné. Ak ho škola má, uveďte ho.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    IČ DPH
                  </label>
                  <input
                    type="text"
                    value={icDph}
                    onChange={(e) => setIcDph(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="napr. SK2021234567"
                  />
                  <p className="text-xs text-gray-500 mt-1">Voliteľné. Vyplňte len ak je škola platiteľom DPH.</p>
                </div>
              </div>

              {/* Official School Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Oficiálny školský e-mail <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={officialEmail}
                  onChange={(e) => setOfficialEmail(e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    validationErrors.officialEmail ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="napr. zslubenik@gmail.com"
                />
                {validationErrors.officialEmail && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.officialEmail}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Tento e-mail bude slúžiť ako hlavný prístupový účet školy k programu.
                </p>
              </div>

              {/* Billing Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fakturačný e-mail <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={billingEmail}
                  onChange={(e) => setBillingEmail(e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    validationErrors.billingEmail ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="napr. ekonomka@zslubenik.sk"
                />
                {validationErrors.billingEmail && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.billingEmail}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Na tento e-mail môžeme zasielať fakturačné alebo objednávkové informácie.
                </p>
              </div>

              {/* Contact Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meno kontaktnej osoby <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    validationErrors.contactName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="napr. Mgr. Jana Nováková"
                />
                {validationErrors.contactName && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.contactName}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Osoba, s ktorou môžeme komunikovať v prípade otázok k objednávke.
                </p>
              </div>

              {/* Phone (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefón
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="napr. +421 900 123 456"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Voliteľné. Pomôže pri rýchlejšom vybavení otázok k objednávke.
                </p>
              </div>

              {/* Note (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Poznámka
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="napr. Prosíme kontaktovať ekonomické oddelenie školy."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Voliteľné. Môžete doplniť organizačné alebo fakturačné poznámky.
                </p>
              </div>
            </div>
          </div>

          {/* Phase 2: Invoice Delivery Preference */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8" data-testid="invoice-delivery-section">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Spôsob doručenia faktúry</h2>

            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={electronicInvoiceConsent}
                  onChange={(e) => setElectronicInvoiceConsent(e.target.checked)}
                  className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  data-testid="school-electronic-consent"
                />
                <span className="text-sm text-gray-700">
                  Súhlasím so zaslaním faktúry v elektronickej forme na uvedený e-mail.
                </span>
              </label>
            </div>

            {!electronicInvoiceConsent && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="school-postal-invoice-section">
                <h3 className="font-semibold text-gray-900 mb-4">Adresa pre poštové doručenie faktúry</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meno príjemcu <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={postalInvoiceName}
                      onChange={(e) => setPostalInvoiceName(e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${validationErrors.postalInvoiceName ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="napr. ZŠ Ľubeník - Ekonomické oddelenie"
                      data-testid="school-postal-name"
                    />
                    {validationErrors.postalInvoiceName && <p className="text-red-500 text-sm mt-1">{validationErrors.postalInvoiceName}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ulica a číslo <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={postalInvoiceStreet}
                      onChange={(e) => setPostalInvoiceStreet(e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${validationErrors.postalInvoiceStreet ? 'border-red-500' : 'border-gray-300'}`}
                      data-testid="school-postal-street"
                    />
                    {validationErrors.postalInvoiceStreet && <p className="text-red-500 text-sm mt-1">{validationErrors.postalInvoiceStreet}</p>}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        PSČ <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={postalInvoicePostalCode}
                        onChange={(e) => setPostalInvoicePostalCode(e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${validationErrors.postalInvoicePostalCode ? 'border-red-500' : 'border-gray-300'}`}
                        data-testid="school-postal-code"
                      />
                      {validationErrors.postalInvoicePostalCode && <p className="text-red-500 text-sm mt-1">{validationErrors.postalInvoicePostalCode}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mesto <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={postalInvoiceCity}
                        onChange={(e) => setPostalInvoiceCity(e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${validationErrors.postalInvoiceCity ? 'border-red-500' : 'border-gray-300'}`}
                        data-testid="school-postal-city"
                      />
                      {validationErrors.postalInvoiceCity && <p className="text-red-500 text-sm mt-1">{validationErrors.postalInvoiceCity}</p>}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Krajina
                    </label>
                    <input
                      type="text"
                      value={postalInvoiceCountry}
                      onChange={(e) => setPostalInvoiceCountry(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      data-testid="school-postal-country"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Terms and Confirmations */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Podmienky používania prístupu</h2>
            
            {/* Usage Restrictions Notice */}
            <div className="mb-6 p-4 bg-amber-50 border border-amber-300 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="text-sm text-gray-800">
                  <p className="leading-relaxed">
                    Platený prístup je určený výhradne pre objednávajúcu školu a len v rozsahu zakúpeného oprávnenia. Prihlasovacie údaje nesmú byť zdieľané, poskytnuté ani sprístupnené žiadnej tretej osobe mimo danej školy. Porušenie tejto podmienky môže viesť k dočasnému obmedzeniu, pozastaveniu alebo deaktivácii prístupu.
                  </p>
                </div>
              </div>
            </div>

            {/* Age Confirmation */}
            <div className="mb-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={ageConfirmed}
                  onChange={(e) => setAgeConfirmed(e.target.checked)}
                  className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">
                  <strong>Potvrdzujem, že som plnoletá osoba (18+) oprávnená konať v mene školy.</strong>
                </span>
              </label>
              {validationErrors.ageConfirmed && (
                <p className="text-red-500 text-sm mt-1 ml-7">{validationErrors.ageConfirmed}</p>
              )}
            </div>

            {/* Terms Acceptance */}
            <div className="mb-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">
                  <strong>Potvrdzujem, že som si prečítal/-a a beriem na vedomie podmienky používania prístupu.</strong>
                </span>
              </label>
              {validationErrors.termsAccepted && (
                <p className="text-red-500 text-sm mt-1 ml-7">{validationErrors.termsAccepted}</p>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-300 rounded-lg p-4 mb-6">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Purchase Button */}
          <div className="text-center">
            <button
              onClick={handlePurchase}
              disabled={isPurchaseDisabled}
              className={`px-12 py-4 rounded-xl font-bold text-lg transition shadow-lg ${
                isPurchaseDisabled
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700 hover:shadow-xl'
              }`}
            >
              {loading ? 'Spracovávam...' : user ? 'Pokračovať na platbu' : 'Prihlásiť sa a pokračovať'}
            </button>
            <p className="text-sm text-gray-500 mt-4">
              Po kliknutí budete presmerovaní na bezpečnú platobnú bránu Stripe
            </p>
          </div>

          {/* Back Link */}
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/financial-literacy')}
              className="text-green-600 hover:text-green-700 font-medium"
            >
              ← Späť na informácie o programe
            </button>
          </div>

        </div>
      </div>
    </div>
  )
}

export default SchoolPurchase
